/**
 * Générateur — Propriétés angulaires des triangles particuliers
 * Attendu ID : 667 | Niveau : 6ème | Domaine : Espace et géométrie
 *
 * Types générés (1 catégorie, 6 formats) :
 *   - nature_triangle_qcm               : QCM 4 choix (identifier la nature)
 *   - angle_canonique_saisie            : saisie numérique (angle 60° équilatéral ou 90° rectangle)
 *   - caracterisations_vf               : V/F 3V + 3F (caractérisations des natures)
 *   - deduction_angle_qcm               : QCM 4 choix (déduction directe d'un angle)
 *   - association_nature_relier         : association "données ↔ nature" (relier)
 *
 * Spécificité : 100 % texte pur (pas de SVG, pas de rapporteur interactif) pour
 * maximiser la stabilité du fingerprint audit. Aucun exercice ne demande un
 * calcul via somme des angles 180° — cette compétence relève de l'attendu 669.
 *
 * Partition avec attendus voisins :
 *   - 666 : construction de triangles.
 *   - 667 : propriétés angulaires des triangles particuliers (ce générateur).
 *   - 669 : somme des angles 180° (calcul via équation 180° − α − β).
 *
 * Variantes estimées : plusieurs milliers par format (objectif : 20/20 uniques
 * sur 20 itérations auditGen, stable sur 5 runs).
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 667;
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split(''); // 23 lettres (exclut I, O)

const L_NAT  = 'Identifier la nature d\'un triangle (QCM)';
const L_ANG  = 'Angle d\'un triangle équilatéral ou rectangle (saisie)';
const L_CAR  = 'Caractérisations (V/F)';
const L_DED  = 'Déduction directe d\'un angle (QCM)';
const L_ASS  = 'Association données ↔ nature (relier)';

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || L_NAT,
});

const pickLetters = (n) => pickLettresSains(LETTRES, n);

// ─── Pools partagés ──────────────────────────────────────────

const LONGUEURS_EX1 = [3, 4, 5, 6, 7, 8, 9, 10];

const TRIPLETS_V1 = {
    equilateral: [[3,3,3], [4,4,4], [5,5,5], [6,6,6], [7,7,7], [8,8,8]],
    isocele:     [[3,3,4], [4,4,5], [4,4,6], [5,5,3], [5,5,4], [5,5,6], [5,5,7], [5,5,8],
                  [6,6,4], [6,6,5], [6,6,7], [6,6,9], [7,7,5], [7,7,6], [7,7,8], [7,7,10]],
    quelconque:  [[3,4,6], [3,5,6], [3,5,7], [4,5,6], [4,5,7], [4,6,7], [4,6,8], [5,6,7],
                  [5,6,8], [5,7,8], [5,7,9], [6,7,8], [6,7,9], [6,8,9], [7,8,9]],
};

const ANGLES_V2 = {
    equilateral: [[60, 60, 60]],
    isocele:     [[30,30,120], [40,40,100], [50,50,80], [70,70,40], [80,80,20],
                  [35,35,110], [65,65,50], [75,75,30], [55,55,70], [25,25,130]],
    rectangle:   [[90, 30, 60], [90, 40, 50], [90, 35, 55], [90, 25, 65],
                  [90, 20, 70], [90, 55, 35], [90, 65, 25], [90, 15, 75]],
    quelconque:  [[40, 55, 85], [40, 60, 80], [40, 65, 75], [30, 65, 85], [35, 60, 85],
                  [25, 70, 85], [45, 55, 80], [30, 70, 80], [35, 65, 80], [25, 75, 80],
                  [50, 55, 75], [50, 60, 70], [45, 50, 85], [45, 65, 70], [35, 70, 75]],
};

const CHOIX_NATURE = ['équilatéral', 'isocèle', 'rectangle', 'quelconque'];

// Pool d'angles pour Ex. 4 C3 : [20..80] \ {45, 60} → 59 valeurs.
// Exclut 45 (rectangle isocèle ambigu) et 60 (cas où 180−2α = α = bonne).
const ANGLES_EX4_C3 = (() => {
    const out = [];
    for (let a = 20; a <= 80; a++) {
        if (a !== 45 && a !== 60) out.push(a);
    }
    return out;
})();

// ─── Ex. 1 — Identifier la nature d'un triangle (QCM 4 choix) ──

const genNature = () => {
    const [L1, L2, L3] = pickLetters(3);
    const r = Math.random();

    let bonne, texte;

    if (r < 0.35) {
        // V1 — 3 côtés donnés
        const nature = randomChoice(['equilateral', 'isocele', 'quelconque']);
        const [a, b, c] = shuffle(randomChoice(TRIPLETS_V1[nature]));
        bonne = nature === 'equilateral' ? 'équilatéral'
              : nature === 'isocele'     ? 'isocèle'
              : 'quelconque';
        texte = `<p>Un triangle ${L1}${L2}${L3} a pour côtés ${L1}${L2} = ${a} cm, ${L2}${L3} = ${b} cm et ${L1}${L3} = ${c} cm. De quelle nature est-il ?</p>`;
    } else if (r < 0.65) {
        // V2 — 3 angles donnés
        const nature = randomChoice(['equilateral', 'isocele', 'rectangle', 'quelconque']);
        const [alpha, beta, gamma] = shuffle(randomChoice(ANGLES_V2[nature]));
        bonne = nature === 'equilateral' ? 'équilatéral'
              : nature === 'isocele'     ? 'isocèle'
              : nature === 'rectangle'   ? 'rectangle'
              : 'quelconque';
        texte = `<p>Dans un triangle ${L1}${L2}${L3}, les angles mesurent ${alpha}°, ${beta}° et ${gamma}°. De quelle nature est ce triangle ?</p>`;
    } else if (r < 0.85) {
        // V3 — angle droit + 2 côtés distincts → rectangle
        let a, b;
        do {
            [a, b] = randomChoiceMultiple(LONGUEURS_EX1, 2);
        } while (a === b);   // a != b : le triangle n'est pas rectangle isocele,
        // ce que les deux longueurs affichees montrent d'elles-memes.
        bonne = 'rectangle';
        texte = `<p>Un triangle ${L1}${L2}${L3} est tel que l'angle $\\widehat{${L1}}$ est droit, ${L1}${L2} = ${a} cm et ${L1}${L3} = ${b} cm. De quelle nature est-il ?</p>`;
    } else {
        // V4 — description qualitative → isocèle
        bonne = 'isocèle';
        texte = `<p>Un triangle ${L1}${L2}${L3} a deux côtés de même longueur et aucun angle droit. De quelle nature est-il ?</p>`;
    }

    // Un triangle équilatéral EST isocèle : proposer les deux ferait deux
    // réponses vraies. Quand la réponse est « équilatéral », « isocèle » cède
    // la place à « rectangle isocèle » : vraie nature du programme, et
    // franchement fausse ici faute d'angle droit.
    const choix = bonne === 'équilatéral'
        ? CHOIX_NATURE.map(c => (c === 'isocèle' ? 'rectangle isocèle' : c))
        : CHOIX_NATURE;
    const payload = makeQcm(choix, bonne, texte, opts(L_NAT));
    payload._meta.generatorKey = 'nature_triangle_qcm';
    return payload;
};

// ─── Ex. 2 — Angle d'un triangle équilatéral ou rectangle (saisie) ──

// Design : 2 variantes paramétrées par lettres (23³ = 12 167 combinaisons),
// tirage 50/50. Les variantes sans lettres (V2/V3 d'une précédente itération)
// ont été retirées car elles introduisaient ~20 % de runs avec collisions
// au-dessous du seuil 20/20 ; V1 et V4 couvrent déjà les deux cas
// pédagogiques (60° équilatéral et 90° rectangle) avec une variété suffisante.
const genAngleCanonique = () => {
    const [L1, L2, L3] = pickLetters(3);
    let bonne, texte;

    if (Math.random() < 0.5) {
        // V1 — équilatéral avec lettres (réponse 60°)
        bonne = 60;
        texte = `<p>Dans un triangle équilatéral ${L1}${L2}${L3}, quelle est la mesure de l'angle $\\widehat{${L2}${L1}${L3}}$ en degrés ?</p>`;
    } else {
        // V4 — rectangle avec lettres (réponse 90°)
        bonne = 90;
        texte = `<p>Dans un triangle ${L1}${L2}${L3} rectangle en ${L1}, quelle est la mesure de l'angle $\\widehat{${L2}${L1}${L3}}$ en degrés ?</p>`;
    }

    const payload = makeSaisie(String(bonne), texte, { ...opts(L_ANG), strict: true, suffixe: ' °' });
    payload._meta.generatorKey = 'angle_canonique_saisie';
    return payload;
};

// ─── Ex. 3 — Caractérisations (V/F 3V + 3F) ──────────────────

// Affirmations : chaque entrée est une fabrique. V3 et F5 sont paramétriques
// (utilisent des lettres). Chaque appel renvoie { id, verite, texte } avec
// verite = 0 (Vrai) ou 1 (Faux), conforme à makeVraiFaux.
const buildAffirmations = () => ({
    VRAIS: {
        V1: () => ({
            id: 'V1', verite: 0,
            texte: 'Dans un triangle équilatéral, chaque angle mesure 60°.',
        }),
        V2: () => ({
            id: 'V2', verite: 0,
            texte: 'Si un triangle a deux côtés de même longueur, alors il est isocèle.',
        }),
        V3: () => {
            const [L1, L2, L3] = pickLetters(3);
            return {
                id: 'V3', verite: 0,
                texte: `Dans un triangle ${L1}${L2}${L3} isocèle en ${L1}, les angles $\\widehat{${L1}${L2}${L3}}$ et $\\widehat{${L1}${L3}${L2}}$ sont égaux.`,
            };
        },
        V4: () => ({
            id: 'V4', verite: 0,
            texte: 'Un triangle dont les trois angles mesurent 60° est équilatéral.',
        }),
        V5: () => ({
            id: 'V5', verite: 0,
            texte: 'Un triangle qui possède un angle droit est appelé triangle rectangle.',
        }),
    },
    FAUX: {
        F1: () => ({
            id: 'F1', verite: 1,
            texte: 'Un triangle équilatéral a un angle droit.',
        }),
        F2: () => ({
            id: 'F2', verite: 1,
            texte: 'Si un triangle a deux angles égaux, alors il est forcément équilatéral.',
        }),
        F3: () => ({
            id: 'F3', verite: 1,
            texte: 'Un triangle isocèle a toujours un angle droit.',
        }),
        F4: () => ({
            id: 'F4', verite: 1,
            texte: 'Dans un triangle équilatéral, un seul angle mesure 60°.',
        }),
        F5: () => {
            const [L1, L2, L3] = pickLetters(3);
            return {
                id: 'F5', verite: 1,
                texte: `Dans un triangle ${L1}${L2}${L3} isocèle en ${L1}, les angles $\\widehat{${L2}${L1}${L3}}$ et $\\widehat{${L1}${L2}${L3}}$ sont égaux.`,
            };
        },
    },
});

const genCaracterisations = () => {
    // Retry loop : garantir 6 lignes distinctes (les templates fixes ne collisionnent
    // jamais entre eux, mais V3 et F5 paramétriques peuvent - très rare - produire
    // des textes ressemblants ; la dédup de makeVraiFaux ramène à 5 lignes si collision).
    let tries = 0;
    while (tries < 5) {
        const builders = buildAffirmations();
        const idsV = randomChoiceMultiple(Object.keys(builders.VRAIS), 3);
        const idsF = randomChoiceMultiple(Object.keys(builders.FAUX), 3);
        const vrais = idsV.map(id => builders.VRAIS[id]());
        const faux  = idsF.map(id => builders.FAUX[id]());
        const mixed = shuffle([...vrais, ...faux]);

        const lignes = mixed.map(a => a.texte);
        const bonnesReponses = mixed.map(a => a.verite);

        if (new Set(lignes).size === lignes.length) {
            const texte = `<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>`;
            const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_CAR));
            payload._meta.generatorKey = 'caracterisations_vf';
            return payload;
        }
        tries++;
    }

    // Fallback : on accepte même si dédup de makeVraiFaux ramène le tableau à 5 lignes.
    const builders = buildAffirmations();
    const vrais = ['V1', 'V2', 'V3'].map(id => builders.VRAIS[id]());
    const faux  = ['F1', 'F3', 'F5'].map(id => builders.FAUX[id]());
    const mixed = shuffle([...vrais, ...faux]);
    const lignes = mixed.map(a => a.texte);
    const bonnesReponses = mixed.map(a => a.verite);
    const texte = `<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>`;
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_CAR));
    payload._meta.generatorKey = 'caracterisations_vf';
    return payload;
};

// ─── Ex. 4 — Déduction directe d'un angle (QCM 4 choix) ──────

// Construit 4 choix pour C3 (isocèle, α = angle à la base) : bonne = α, avec 3
// distracteurs tirés d'un pool ordonné de pièges pédagogiques classiques.
// Garanties : 4 valeurs distinctes, strictement > 0 et ≤ 180°.
const buildChoixC3 = (alpha) => {
    const bonne = alpha;
    const candidats = [
        180 - 2 * alpha,   // piège somme 180° (angle au sommet principal)
        180 - alpha,       // piège complément à plat
        90 - alpha,        // piège complément à droit
        2 * alpha,         // piège doublement
        alpha + 10,        // piège arithmétique +10
        alpha - 10,        // piège arithmétique -10
        alpha + 5,         // fallback +5
        alpha - 5,         // fallback -5
    ];
    const distracteurs = [];
    const dejaVu = new Set([bonne]);
    for (const c of candidats) {
        if (c > 0 && c <= 180 && !dejaVu.has(c)) {
            distracteurs.push(c);
            dejaVu.add(c);
            if (distracteurs.length === 3) break;
        }
    }
    return [bonne, ...distracteurs].map(v => `${v}°`);
};

const genDeduction = () => {
    const r = Math.random();
    const sousCas = r < 0.30 ? 'C1' : (r < 0.60 ? 'C2' : 'C3');
    const [L1, L2, L3] = pickLetters(3);

    let choix, bonne, texte;
    if (sousCas === 'C1') {
        bonne = '60°';
        choix = ['60°', '90°', '120°', '30°'];
        texte = `<p>Le triangle ${L1}${L2}${L3} est équilatéral. Quelle est la mesure de l'angle $\\widehat{${L2}${L1}${L3}}$ ?</p>`;
    } else if (sousCas === 'C2') {
        bonne = '90°';
        choix = ['90°', '60°', '45°', '180°'];
        texte = `<p>Le triangle ${L1}${L2}${L3} est rectangle en ${L1}. Quelle est la mesure de l'angle $\\widehat{${L2}${L1}${L3}}$ ?</p>`;
    } else {
        const alpha = randomChoice(ANGLES_EX4_C3);
        bonne = `${alpha}°`;
        choix = buildChoixC3(alpha);
        texte = `<p>Le triangle ${L1}${L2}${L3} est isocèle en ${L1}, et l'angle $\\widehat{${L1}${L2}${L3}}$ mesure ${alpha}°. Quelle est la mesure de l'angle $\\widehat{${L1}${L3}${L2}}$ ?</p>`;
    }

    const payload = makeQcm(choix, bonne, texte, opts(L_DED));
    payload._meta.generatorKey = 'deduction_angle_qcm';
    return payload;
};

// ─── Ex. 6 — Association données ↔ nature (relier) ───────────

// D1 (équilatéral) : côté a ∈ [3..9]
const POOL_D1 = [3, 4, 5, 6, 7, 8, 9];

// D2 (isocèle) : (b, c) avec b ≠ c, 2b > c (faisabilité : 3e côté < somme).
// Pool pré-calculé.
const POOL_D2 = [
    [3, 2], [3, 4], [3, 5],
    [4, 2], [4, 3], [4, 5], [4, 6], [4, 7],
    [5, 2], [5, 3], [5, 4], [5, 6], [5, 7], [5, 8], [5, 9],
    [6, 2], [6, 3], [6, 4], [6, 5], [6, 7], [6, 8], [6, 9], [6, 10],
    [7, 2], [7, 3], [7, 4], [7, 5], [7, 6], [7, 8], [7, 9], [7, 10],
    [8, 2], [8, 3], [8, 4], [8, 5], [8, 6], [8, 7], [8, 9], [8, 10],
];

// D3 (rectangle) : (d, e) avec d ≠ e, d,e ∈ [3..9] → 42 paires.
const POOL_D3 = (() => {
    const out = [];
    for (let d = 3; d <= 9; d++) {
        for (let e = 3; e <= 9; e++) {
            if (d !== e) out.push([d, e]);
        }
    }
    return out;
})();

// D4 (quelconque) : (f, g, h) tous distincts, ∈ [3..9], NON pythagoriciens,
// avec faisabilité (max < somme des deux autres).
const POOL_D4 = (() => {
    const out = [];
    for (let f = 3; f <= 9; f++) {
        for (let g = f + 1; g <= 9; g++) {
            for (let h = g + 1; h <= 9; h++) {
                // Exclure {3,4,5} (seul triplet pythagoricien dans [3..9]).
                if (f === 3 && g === 4 && h === 5) continue;
                // Faisabilité : h < f + g (car f < g < h).
                if (h < f + g) {
                    out.push([f, g, h]);
                }
            }
        }
    }
    return out;
})();

const genAssociation = () => {
    const a = randomChoice(POOL_D1);
    const [b, c] = randomChoice(POOL_D2);
    const [d, e] = randomChoice(POOL_D3);
    const [f, g, h] = randomChoice(POOL_D4);

    // `source` = description (colonne gauche) ; `target` = nature (colonne droite).
    const pairs = [
        { source: `Triangle de côtés ${a} cm, ${a} cm, ${a} cm.`,                                          target: 'équilatéral' },
        { source: `Triangle de côtés ${b} cm, ${b} cm, ${c} cm.`,                                          target: 'isocèle'     },
        { source: `Triangle ayant un angle de 90° et deux côtés de longueurs ${d} cm et ${e} cm.`,         target: 'rectangle'   },
        { source: `Triangle de côtés ${f} cm, ${g} cm, ${h} cm.`,                                          target: 'quelconque'  },
    ];

    const texte = `<p>Associe chaque description de triangle à sa nature.</p>`;
    const payload = makeRelier(pairs, texte, opts(L_ASS));
    payload._meta.generatorKey = 'association_nature_relier';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Identification de la nature via figure SVG (codage graphique)
// ═══════════════════════════════════════════════════════════

const L_NAT_SVG = 'Identifier la nature d\'un triangle (figure)';

const svgToDataUrl = (svg) => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

// Dessine un petit tick perpendiculaire au milieu du segment [P1,P2] (marque d'égalité).
const tickMark = (P1, P2) => {
    const mx = (P1.x + P2.x) / 2;
    const my = (P1.y + P2.y) / 2;
    const dx = P2.x - P1.x, dy = P2.y - P1.y;
    const L = Math.hypot(dx, dy);
    const px = (-dy / L) * 5;
    const py = (dx / L) * 5;
    return `<line x1='${(mx - px).toFixed(1)}' y1='${(my - py).toFixed(1)}' x2='${(mx + px).toFixed(1)}' y2='${(my + py).toFixed(1)}' stroke='#E24B4A' stroke-width='1.6'/>`;
};

// Dessine le petit carré d'angle droit au sommet A entre [AB) et [AC).
const rightAngleSquare = (A, B, C) => {
    const size = 11;
    const dABx = B.x - A.x, dABy = B.y - A.y;
    const LAB = Math.hypot(dABx, dABy);
    const ux = dABx / LAB, uy = dABy / LAB;
    const dACx = C.x - A.x, dACy = C.y - A.y;
    const LAC = Math.hypot(dACx, dACy);
    const vx = dACx / LAC, vy = dACy / LAC;
    const P1 = { x: A.x + ux * size, y: A.y + uy * size };
    const P2 = { x: A.x + vx * size, y: A.y + vy * size };
    const P3 = { x: P1.x + vx * size, y: P1.y + vy * size };
    return `<path d='M ${P1.x.toFixed(1)},${P1.y.toFixed(1)} L ${P3.x.toFixed(1)},${P3.y.toFixed(1)} L ${P2.x.toFixed(1)},${P2.y.toFixed(1)}' fill='none' stroke='black' stroke-width='1.3'/>`;
};

// Rotation 2D autour du centre (Cx, Cy) — convention SVG (y inversé).
const rotatePoint = (p, Cx, Cy, rotRad) => ({
    x: Cx + (p.x - Cx) * Math.cos(rotRad) - (p.y - Cy) * Math.sin(rotRad),
    y: Cy + (p.x - Cx) * Math.sin(rotRad) + (p.y - Cy) * Math.cos(rotRad),
});

/**
 * Construit un triangle d'une nature donnée. Tous paramètres aléatoires (taille,
 * proportions) + rotation globale → variété quasi-infinie.
 */
const buildTriangleByNature = (nature) => {
    const Cx = 120, Cy = 110;
    const rotRad = (randInt(0, 359) * Math.PI) / 180;
    let rawPts;

    if (nature === 'equilateral') {
        const R = randInt(60, 78);
        rawPts = [90, 210, 330].map((aDeg) => {
            const rad = (aDeg * Math.PI) / 180;
            return { x: Cx + R * Math.cos(rad), y: Cy + R * Math.sin(rad) };
        });
    } else if (nature === 'isocele') {
        const h = randInt(60, 85);
        // b borné par h/2,1. Les deux côtés égaux valent hypot(b, h) et la base
        // vaut 2b : ils coïncident quand b = h/√3 ≈ h/1,73, et le triangle est
        // alors ÉQUILATÉRAL alors qu'il n'est marqué que sur deux côtés et
        // étiqueté « Isocèle ». 10,8 % de l'ancien domaine tombait à moins de 2 %
        // de ce cas. Ici il reste 14 % d'écart entre côté et base, et l'angle le
        // plus grand se tient à 14,5° de l'angle droit.
        const b = randInt(22, Math.floor(h / 2.1));
        rawPts = [
            { x: Cx, y: Cy - h * 0.55 },          // sommet haut
            { x: Cx - b, y: Cy + h * 0.45 },      // base gauche
            { x: Cx + b, y: Cy + h * 0.45 },      // base droite
        ];
    } else if (nature === 'rectangle') {
        // Rectangle strict (non isocèle) : force |l1 - l2| >= 15 pour éviter l'ambiguïté.
        const l1 = randInt(80, 105);
        const l2 = randInt(45, 65);
        const Ax = Cx - l1 / 3;
        const Ay = Cy + l2 / 3;
        rawPts = [
            { x: Ax, y: Ay },             // A (angle droit)
            { x: Ax + l1, y: Ay },        // B
            { x: Ax, y: Ay - l2 },        // C
        ];
    } else if (nature === 'rectangle_isocele') {
        // Rectangle isocèle : 2 cathètes égales (l1 == l2), angles 90°/45°/45°.
        const l = randInt(60, 85);
        const Ax = Cx - l / 3;
        const Ay = Cy + l / 3;
        rawPts = [
            { x: Ax, y: Ay },             // A (angle droit)
            { x: Ax + l, y: Ay },         // B
            { x: Ax, y: Ay - l },         // C
        ];
    } else {
        // quelconque : ni côté égal, ni angle droit — et il faut que ça tienne
        // APRÈS le bruit. Le triangle de base précédent avait un angle de 86,9°,
        // à 3° de l'angle droit, et un bruit de ±5 px : 6,4 % des figures
        // annoncées « Quelconque » étaient en fait rectangles, et l'élève qui
        // mesurait était compté faux.
        // Angles 45° / 58° / 77° : le plus grand reste à 13° de l'angle droit et
        // les trois côtés diffèrent d'au moins 11 %. Bruit ramené à ±3 px.
        // Marges mesurées sur 40 000 tirages bruités : 5,7° et 4,9 %.
        rawPts = [
            { x: 51 + randInt(-3, 3), y: 136 + randInt(-3, 3) },
            { x: 179 + randInt(-3, 3), y: 136 + randInt(-3, 3) },
            { x: 130 + randInt(-3, 3), y: 57 + randInt(-3, 3) },
        ];
    }
    return rawPts.map(p => rotatePoint(p, Cx, Cy, rotRad));
};

const svgTriangleNature = (nature, salt) => {
    const [A, B, C] = buildTriangleByNature(nature);

    let marks = '';
    if (nature === 'equilateral') {
        marks = tickMark(A, B) + tickMark(B, C) + tickMark(A, C);
    } else if (nature === 'isocele') {
        // Les 2 côtés partant du sommet A (haut) sont égaux
        marks = tickMark(A, B) + tickMark(A, C);
    } else if (nature === 'rectangle') {
        marks = rightAngleSquare(A, B, C);
    } else if (nature === 'rectangle_isocele') {
        // Carré d'angle droit + ticks sur les 2 cathètes (AB et AC), toutes 2 égales
        marks = rightAngleSquare(A, B, C) + tickMark(A, B) + tickMark(A, C);
    }

    const tri = `M ${A.x.toFixed(1)},${A.y.toFixed(1)} L ${B.x.toFixed(1)},${B.y.toFixed(1)} L ${C.x.toFixed(1)},${C.y.toFixed(1)} Z`;
    return `<svg data-v='${salt}' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 220' width='240' height='220'>`
        + `<path d='${tri}' fill='none' stroke='black' stroke-width='1.8' stroke-linejoin='round'/>`
        + marks
        + `</svg>`;
};

const NATURES_SVG = ['equilateral', 'isocele', 'rectangle', 'rectangle_isocele', 'quelconque'];
const LABELS_NATURES = {
    equilateral: 'Équilatéral',
    isocele: 'Isocèle',
    rectangle: 'Rectangle',
    rectangle_isocele: 'Rectangle isocèle',
    quelconque: 'Quelconque',
};

const genNatureSvgQcm = () => {
    const nature = randomChoice(NATURES_SVG);
    const salt = randInt(1, 99999);
    const svg = svgTriangleNature(nature, salt);
    const imageData = svgToDataUrl(svg);

    // Même raison : un équilatéral est isocèle. On retire « Isocèle » quand la
    // figure est équilatérale ; il n'existe pas de sixième nature à mettre à la
    // place, le QCM passe donc à 4 options dans ce cas.
    const choix = Object.values(LABELS_NATURES)
        .filter(l => !(nature === 'equilateral' && l === LABELS_NATURES.isocele));
    const bonne = LABELS_NATURES[nature];

    const texte = `<p>Observe le triangle ci-dessous. Quelle est sa nature ?</p>{{element:0}}`;

    const payload = makeQcm(choix, bonne, texte, opts(L_NAT_SVG));
    payload.elements_additionnels = {
        version: 1,
        elements: [{ type: 'image', config: { imageData } }],
    };
    payload._meta.generatorKey = 'nature_triangle_svg_qcm';
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    nature_triangle_qcm:              genNature,
    nature_triangle_svg_qcm:          genNatureSvgQcm,
    angle_canonique_saisie:           genAngleCanonique,
    caracterisations_vf:              genCaracterisations,
    deduction_angle_qcm:              genDeduction,
    association_nature_relier:        genAssociation,
};

export const CATEGORIES = [
    {
        id: 'identifier_nature_triangle',
        label: 'Identifier la nature d\'un triangle',
        formatsList: [
            { key: 'nature_triangle_qcm',       label: 'QCM' },
            { key: 'nature_triangle_svg_qcm',   label: 'QCM figure' },
            { key: 'association_nature_relier', label: 'Relier' },
        ],
    },
    {
        id: 'proprietes_angulaires_triangles',
        label: 'Propriétés angulaires des triangles particuliers',
        formatsList: [
            { key: 'angle_canonique_saisie', label: 'Saisie' },
            { key: 'deduction_angle_qcm',    label: 'QCM' },
            { key: 'caracterisations_vf',    label: 'V/F' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

// Exposé pour tests unitaires (non utilisé en production).
export const _internals = {
    LETTRES,
    TRIPLETS_V1,
    ANGLES_V2,
    ANGLES_EX4_C3,
    CHOIX_NATURE,
    POOL_D1,
    POOL_D2,
    POOL_D3,
    POOL_D4,
    buildChoixC3,
    buildAffirmations,
    genNature,
    genAngleCanonique,
    genCaracterisations,
    genDeduction,
    genAssociation,
};
